import { useEffect, useState } from "react";

import { useLocation } from "react-router-dom";

import { partnerService } from "../services/partnerService";

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Initialize and manage Google Analytics (gtag.js) with partner tracking.
 * Must be used inside a <Router>.
 */
export const useGtag = (enabled = true) => {
  const location = useLocation(); // ✅ always call at top-level (no try/catch)
  const [path, setPath] = useState(location.pathname + location.search);

  useEffect(() => {
    if (!enabled) return;

    // --- Initialize dataLayer ---
    if (!window.dataLayer) window.dataLayer = [];

    // --- Initialize gtag ---
    if (!window.gtag) {
      window.gtag = function (...args: any[]) {
        window.dataLayer.push(args);
      };
      window.gtag("js", new Date());
    }

    const getPartnerName = () => {
      const partner = partnerService.getPartner();
      if (partner?.name) return partner.name;

      const searchParams = new URLSearchParams(window.location.search);
      const partnerQuery = searchParams.get("partner");
      const utmSource = searchParams.get("utm_source")?.trim();
      return partnerQuery || utmSource || "Webview";
    };

    // --- Patch dataLayer.push ---
    const originalPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = function (...args: any[]) {
      const firstArg = args[0];
      if (firstArg && firstArg[0] === "event") {
        const params = firstArg[3] || firstArg[2] || {};
        if (!params.partner_name) params.partner_name = getPartnerName();
      }
      return originalPush(...args);
    };

    // --- Patch gtag() ---
    const originalGtag = window.gtag.bind(window);
    window.gtag = function (...args: any[]) {
      if (args[0] === "event" && args[2] && !args[2].partner_name) {
        args[2].partner_name = getPartnerName();
      }
      if (args[0] === "config" && args[2] && !args[2].partner_name) {
        args[2].partner_name = getPartnerName();
      }
      return originalGtag(...args);
    };

    const env = process.env.REACT_APP_BUILD_ENV;
    const gaId =
      env === "dev" || env === "uat" ? "G-FEH29QWX9Z" : "G-D8TWGYCEYD";

    const partnerName = getPartnerName();
    window.gtag("config", gaId, {
      send_page_view: false,
      partner_name: partnerName,
    });
  }, [enabled]);

  // --- Track page changes ---
  useEffect(() => {
    if (!enabled || !window.gtag) return;

    const partnerName =
      partnerService.getPartner()?.name ||
      new URLSearchParams(window.location.search).get("partner") ||
      new URLSearchParams(window.location.search).get("utm_source")?.trim() ||
      "Webview";

    const env = process.env.REACT_APP_BUILD_ENV;
    const gaId =
      env === "dev" || env === "uat" ? "G-FEH29QWX9Z" : "G-D8TWGYCEYD";

    window.gtag("config", gaId, {
      page_path: path,
      page_title: document.title,
      partner_name: partnerName,
    });
  }, [enabled, path]);

  // --- Update path on route change ---
  useEffect(() => {
    setPath(location.pathname + location.search);
  }, [location]);
};
